import { logger } from '@adornis/base/logging.js';
import type { Dictionary, Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { StudentFilter } from '@chancenwerk/db/filter/StudentFilter.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { catchError, from, of, switchMap } from 'rxjs';
import { CourseFilter } from '../filter/CourseFilter.js';
import { Course } from './Course.js';

export enum Subject {
  DEUTSCH = 'Deutsch',
  MATHE = 'Mathe',
  ENGLISH = 'Englisch',
  DAZ = 'Deutsch Basic-Kurs (DaZ) ',
  FRANZOESISCH = 'Französisch ',
  SPANISCH = 'Spanisch',
  LATEIN = 'Latein',

  // These for student formular
  CHEMIE = 'Chemie',
  BIOLOGIE = 'Biologie',
  PHYSIK = 'Physik',

  // This for courses. Why? I dont fucking know
  NATURWISSENSCHAFTEN = 'Naturwissenschaften',
}

@Entity()
export class CourseDigitalFilter extends CourseFilter {
  static override _class = 'CourseDigitalFilter';

  constructor(doc: Partial<EntityData<CourseDigitalFilter>>) {
    super(doc);
  }

  @Field(type => [String])
  subjects?: string[];

  @Field(type => Int)
  jahrgangsstufe?: Int;

  private static _getSubjectFilter(subject: string) {
    if (subject === Subject.BIOLOGIE || subject === Subject.CHEMIE || subject === Subject.PHYSIK)
      return Subject.NATURWISSENSCHAFTEN;
    return subject;
  }

  private static _buildSubjects(subjects: string[]) {
    const filter = [] as any[];
    for (const subject of subjects) {
      if (subject === '') continue;
      if (subject === Subject.DEUTSCH) {
        filter.push(this._getSubjectFilter(Subject.DAZ));
      }
      filter.push(this._getSubjectFilter(subject));
    }
    return filter;
  }

  override getMongoFilter(): Dictionary<any> {
    const obj = this.toObject();
    let dic = super.getMongoFilter();

    dic._class = CourseDigital._class;

    if (obj.jahrgangsstufe) {
      dic.jahrgangsstufe = obj.jahrgangsstufe;
    }

    if (obj.subjects && obj.subjects.length > 0) {
      dic.subject = { $in: CourseDigitalFilter._buildSubjects(obj.subjects) };
    }

    return dic;
  }
}

/**
 * @entity CourseDigital
 */
@Entity()
export class CourseDigital extends Course {
  public static override _class = 'CourseDigital';

  constructor(doc: PartialEntityData<CourseDigital>) {
    super(doc);
  }
  @Field(type => [String], { default: v => v ?? [] })
  public linkedKPFIDs: Maybe<string[]>;

  @DataLoadedField(type => [KPF], ({ linkedKPFIDs }) => linkedKPFIDs ?? [])
  public linkedKPFs: Maybe<KPF[]>;

  @Field(type => String)
  public subject: Maybe<Subject>;

  @Field(type => Int)
  public jahrgangsstufe?: number;

  @Field(type => Boolean, { default: v => v ?? false })
  public changeNotification?: boolean;

  @DataLoadedField(type => [ExternerMitarbeiter], ({ diKuIDs }) => diKuIDs ?? [])
  public diKus: Maybe<ExternerMitarbeiter[]>;

  @Field(type => [String], { default: v => v ?? [] })
  public diKuIDs: Maybe<string[]>;

  @Subscription(type => [CourseDigital])
  public static subscribeDigitialCourseByKPFID(@Arg('kpfID', type => String) kpfID: string) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const selector = { _class: 'CourseDigital', linkedKPFIDs: kpfID };
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }

  @Query(type => Int)
  public static getDigitalCourseOpenStudents(@Arg('courseID', type => String) courseID: string) {
    return async () => {
      const course = await this.getByID<CourseDigital>(courseID)({
        subject: 1,
        jahrgangsstufe: 1,
        osStudentIDs: 1,
      });

      if (!course) return 0;

      const filter = new StudentFilter({
        active: true,
        studentType: true,
        jahrgang: course.jahrgangsstufe,
        preferedSubject: course.subject ? [course.subject] : undefined,
      });

      const col = await getRawCollection(Student._collectionName);
      const res = await col
        .aggregate([
          {
            $match: {
              ...filter.getMongoFilter(),
              _id: { $nin: course.osStudentIDs ?? [] },
            },
          },
          { $project: { _id: 1 } },
          { $count: 'count' },
        ])
        .toArray();

      return res[0]?.count ?? 0;
    };
  }

  private static _getSubjectFilter(subject: string) {
    if (subject === Subject.BIOLOGIE || subject === Subject.CHEMIE || subject === Subject.PHYSIK)
      return { subject: Subject.NATURWISSENSCHAFTEN };
    return { subject };
  }

  private static _buildOrFilter(subjects: string[]) {
    const filter = [] as any[];
    for (const subject of subjects) {
      if (subject === '') continue;
      if (subject === Subject.DEUTSCH) {
        filter.push(this._getSubjectFilter(Subject.DAZ));
      }
      filter.push(this._getSubjectFilter(subject));
    }
    return filter;
  }

  @Subscription(type => [CourseDigital])
  public static subscribeDigitialCourseSubjectAndGrade(
    @Arg('subjects', type => [String]) subjects: string[],
    @Arg('jahrgangsstufe', type => Int) jahrgangsstufe: number,
  ) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const selector = {
        _class: 'CourseDigital',
        $and: [
          { $or: [{ jahrgangsstufe: { $lte: jahrgangsstufe } }, { jahrgangsstufe: null }] },
          { $or: this._buildOrFilter(subjects) },
        ],
      };
      if (!selector.$and.at(1)?.$or.length) {
        // * When no subjects is selected, show all:
        // selector.$and.splice(1, 1);

        // * Don't show any if none are selected
        selector.$and.at(1)!.$or = [{ subject: 'NONE' }];
      }
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }

  @Subscription(type => [CourseDigital])
  public static subscribeDigitalCourseBySearch(
    @Arg('filter', type => CourseDigitalFilter) filter: CourseDigitalFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<CourseDigital>) => {
      const handle = getCollectionHandle<CourseDigital>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(
            publishFilter ? { $and: [publishFilter, filter.getMongoFilter()] } : filter.getMongoFilter(),
          );
        }),
        catchError(e => {
          logger.error(e);
          return of([] as CourseDigital[]);
        }),
      );
    };
  }
}
